import * as THREE from "three";

export type WalkCameraOptions = {
  /** Base walk speed in world units per second. */
  walkSpeed?: number;
  /** Multiplier applied to walk speed while Shift is held. */
  shiftMultiplier?: number;
  /** Radians of rotation per pixel of mouse movement. */
  rotSpeed?: number;
  /** Maximum absolute pitch, in radians. */
  maxPitch?: number;
  /** Called whenever pointer-lock state changes. */
  onLockChange?: (locked: boolean) => void;
};

const WALK_KEYS = new Set(["w", "a", "s", "d", "q", "e"]);
const WORLD_UP = new THREE.Vector3(0, 0, 1);

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
};

/**
 * Encapsulates the "FPS walk" input handling for a Three.js camera:
 *   - click + drag on the canvas to look around (via pointer lock)
 *   - WASD to move horizontally, Q/E to move vertically
 *   - Shift to move faster
 *
 * The camera is assumed to have `up = (0, 0, 1)` (Z-up world). Call
 * {@link update} once per animation frame and {@link dispose} when done.
 */
export class WalkCameraController {
  private readonly camera: THREE.PerspectiveCamera;
  private readonly domElement: HTMLElement;

  private readonly walkSpeed: number;
  private readonly shiftMultiplier: number;
  private readonly rotSpeed: number;
  private readonly maxPitch: number;
  private readonly onLockChange?: (locked: boolean) => void;

  private readonly lookDir = new THREE.Vector3(1, 0, 0);

  private readonly pressed = new Set<string>();
  private shiftDown = false;
  private dragging = false;
  private enabled = true;
  private lookDirty = false;

  private readonly disposers: Array<() => void> = [];

  // Scratch vectors reused each frame
  private readonly yawQ = new THREE.Quaternion();
  private readonly pitchQ = new THREE.Quaternion();
  private readonly rightAxis = new THREE.Vector3();
  private readonly forwardHoriz = new THREE.Vector3();
  private readonly rightVec = new THREE.Vector3();
  private readonly move = new THREE.Vector3();
  private readonly tmpTarget = new THREE.Vector3();

  constructor(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    options: WalkCameraOptions = {},
  ) {
    this.camera = camera;
    this.domElement = domElement;
    this.walkSpeed = options.walkSpeed ?? 2000;
    this.shiftMultiplier = options.shiftMultiplier ?? 3;
    this.rotSpeed = options.rotSpeed ?? 0.003;
    this.maxPitch = options.maxPitch ?? Math.PI / 2 - 0.01;
    this.onLockChange = options.onLockChange;

    camera.up.set(0, 0, 1);
    this.applyLook();
    this.installListeners();
  }

  /**
   * Aim the camera at the given world point (only the horizontal direction
   * is used). Call this whenever you (re)position the camera.
   */
  setLookTarget(target: THREE.Vector3): void {
    const dx = target.x - this.camera.position.x;
    const dy = target.y - this.camera.position.y;
    if (Math.hypot(dx, dy) > 1e-3) {
      this.lookDir.set(dx, dy, 0).normalize();
      this.applyLook();
    }
  }

  /**
   * Aim the camera along the given horizontal world-space direction (only the
   * XY components are used). No-op while the pointer is locked so the user's
   * live look input is not fought.
   */
  setLookDirection(direction: THREE.Vector3): void {
    if (this.isLocked) return;
    const dx = direction.x;
    const dy = direction.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const nx = dx / len;
    const ny = dy / len;
    const pitchZ = THREE.MathUtils.clamp(this.lookDir.z, -1, 1);
    const horizontalScale = Math.sqrt(Math.max(0, 1 - pitchZ * pitchZ));
    const nextX = nx * horizontalScale;
    const nextY = ny * horizontalScale;
    // Skip if already aligned (avoids feedback with 3D-camera update loop).
    if (
      Math.abs(nextX - this.lookDir.x) < 1e-4 &&
      Math.abs(nextY - this.lookDir.y) < 1e-4
    ) {
      return;
    }
    this.lookDir.set(nextX, nextY, pitchZ).normalize();
    this.lookDirty = true;
  }

  /** True while the pointer is locked to the canvas (i.e. the user is looking around). */
  get isLocked(): boolean {
    return document.pointerLockElement === this.domElement;
  }

  /** Advance the controller by `dt` seconds. Returns true if the camera moved or rotated. */
  update(dt: number): boolean {
    if (!this.enabled) return false;
    const hasMovement = this.pressed.size > 0;
    if (hasMovement) this.applyMovement(dt);
    if (hasMovement || this.lookDirty) {
      this.applyLook();
      this.lookDirty = false;
      return true;
    }
    return false;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.pressed.clear();
      this.shiftDown = false;
      this.dragging = false;
      if (this.isLocked) document.exitPointerLock?.();
    }
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers.length = 0;
    if (this.isLocked) document.exitPointerLock?.();
    this.pressed.clear();
    this.shiftDown = false;
    this.dragging = false;
  }

  // ---------- private ----------

  private applyLook(): void {
    this.tmpTarget.copy(this.camera.position).add(this.lookDir);
    this.camera.lookAt(this.tmpTarget);
  }

  private applyMovement(dt: number): void {
    this.forwardHoriz.copy(this.lookDir);
    this.forwardHoriz.z = 0;
    if (this.forwardHoriz.lengthSq() < 1e-8) this.forwardHoriz.set(1, 0, 0);
    this.forwardHoriz.normalize();
    this.rightVec.crossVectors(this.forwardHoriz, WORLD_UP);
    if (this.rightVec.lengthSq() < 1e-8) this.rightVec.set(0, 1, 0);
    this.rightVec.normalize();

    const speed = this.walkSpeed * (this.shiftDown ? this.shiftMultiplier : 1);
    const step = speed * dt;

    this.move.set(0, 0, 0);
    if (this.pressed.has("w")) this.move.addScaledVector(this.forwardHoriz, step);
    if (this.pressed.has("s")) this.move.addScaledVector(this.forwardHoriz, -step);
    if (this.pressed.has("d")) this.move.addScaledVector(this.rightVec, step);
    if (this.pressed.has("a")) this.move.addScaledVector(this.rightVec, -step);
    if (this.pressed.has("q")) this.move.z += step;
    if (this.pressed.has("e")) this.move.z -= step;

    this.camera.position.add(this.move);
  }

  private applyMouseLook(dx: number, dy: number): void {
    if (dx !== 0) {
      this.yawQ.setFromAxisAngle(WORLD_UP, -dx * this.rotSpeed);
      this.lookDir.applyQuaternion(this.yawQ).normalize();
    }
    if (dy !== 0) {
      this.rightAxis.crossVectors(this.lookDir, WORLD_UP);
      if (this.rightAxis.lengthSq() < 1e-8) this.rightAxis.set(1, 0, 0);
      this.rightAxis.normalize();
      const currentPitch = Math.asin(
        THREE.MathUtils.clamp(this.lookDir.z, -1, 1),
      );
      const desiredPitch = THREE.MathUtils.clamp(
        currentPitch + -dy * this.rotSpeed,
        -this.maxPitch,
        this.maxPitch,
      );
      const deltaPitch = desiredPitch - currentPitch;
      if (Math.abs(deltaPitch) > 1e-6) {
        this.pitchQ.setFromAxisAngle(this.rightAxis, deltaPitch);
        this.lookDir.applyQuaternion(this.pitchQ).normalize();
      }
    }
    this.lookDirty = true;
  }

  private installListeners(): void {
    const el = this.domElement;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!this.enabled || isEditableTarget(e.target)) return;
      if (e.key === "Shift") {
        this.shiftDown = true;
        return;
      }
      const key = e.key.toLowerCase();
      if (!WALK_KEYS.has(key)) return;
      this.pressed.add(key);
      e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        this.shiftDown = false;
        return;
      }
      this.pressed.delete(e.key.toLowerCase());
    };
    const onBlur = () => {
      this.pressed.clear();
      this.shiftDown = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    this.disposers.push(() => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    });

    const onPointerDown = (e: PointerEvent) => {
      if (!this.enabled || e.button !== 0) return;
      this.dragging = true;
      el.requestPointerLock?.();
      e.preventDefault();
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!this.dragging) return;
      if (!(e.buttons & 1)) return;
      const { movementX: dx, movementY: dy } = e;
      if (dx === 0 && dy === 0) return;
      this.applyMouseLook(dx, dy);
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return;
      document.exitPointerLock?.();
      this.dragging = false;
    };
    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("contextmenu", onContextMenu);
    this.disposers.push(() => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("contextmenu", onContextMenu);
    });

    const onPointerLockChange = () => {
      const locked = this.isLocked;
      if (!locked) this.dragging = false;
      this.onLockChange?.(locked);
    };
    document.addEventListener("pointerlockchange", onPointerLockChange);
    this.disposers.push(() => {
      document.removeEventListener("pointerlockchange", onPointerLockChange);
    });
  }
}
